import { css } from '@emotion/react';
import { type FC, type HTMLAttributes } from 'react';

export type ProportionSegment = {
  label: string;
  /** Share of the whole, in any unit; segments are sized relative to the sum. */
  value: number;
  /** CSS color. Defaults step through the primary scale. */
  color?: string;
};

export type ProportionBarProps = HTMLAttributes<HTMLDivElement> & {
  segments: ProportionSegment[];
  /** Accessible summary of the whole bar, e.g. "Montana land ownership". */
  label: string;
  /** Show each segment's percentage and label under the bar. */
  showLegend?: boolean;
  /** Use `inverse` on dark surfaces. */
  tone?: 'default' | 'inverse';
};

const DEFAULT_COLORS = {
  default: [
    'var(--color-primary-500)',
    'var(--color-primary-300)',
    'var(--color-primary-100)',
    'var(--color-neutral-200)',
  ],
  inverse: [
    'var(--color-primary-100)',
    'var(--color-primary-300)',
    'var(--color-primary-500)',
    'var(--color-neutral-500)',
  ],
};

const barStyles = css`
  display: flex;
  gap: 4px;
  height: 14px;
  overflow: hidden;
  border-radius: var(--radius-round);
`;

const legendStyles = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--spacing-6);
  margin: var(--spacing-5) 0 0;
  padding: 0;
  list-style: none;
`;

const percent = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 0,
});

/**
 * A single horizontal bar split into proportional segments, with an optional
 * legend of large percentages.
 */
const ProportionBar: FC<ProportionBarProps> = ({
  segments,
  label,
  showLegend = true,
  tone = 'default',
  className,
  ...rest
}) => {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  if (!(total > 0)) return null;

  const colored = segments.map((segment, index) => ({
    ...segment,
    share: segment.value / total,
    color:
      segment.color ??
      DEFAULT_COLORS[tone][index % DEFAULT_COLORS[tone].length],
  }));
  const summary = `${label}: ${colored
    .map((s) => `${s.label} ${percent.format(s.share)}`)
    .join(', ')}`;

  return (
    <div {...rest} className={className}>
      <div role="img" aria-label={summary} css={barStyles}>
        {colored.map((segment) => (
          <span
            key={segment.label}
            css={{ flex: `${segment.share} 1 0`, background: segment.color }}
          />
        ))}
      </div>
      {showLegend && (
        <ul css={legendStyles} aria-hidden="true">
          {colored.map((segment) => (
            <li key={segment.label}>
              <p
                css={{
                  margin: 0,
                  fontSize: 48,
                  lineHeight: 1,
                  fontWeight: 800,
                  color:
                    tone === 'inverse'
                      ? 'var(--color-base-white)'
                      : 'var(--color-primary-800)',
                }}
              >
                {percent.format(segment.share)}
              </p>
              <p
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  margin: '8px 0 0',
                  fontSize: 15,
                  color:
                    tone === 'inverse'
                      ? 'var(--color-primary-100)'
                      : 'var(--color-neutral-800)',
                }}
              >
                <span
                  css={{
                    width: 10,
                    height: 10,
                    borderRadius: 3,
                    background: segment.color,
                  }}
                />
                {segment.label}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

ProportionBar.displayName = 'ProportionBar';

export default ProportionBar;
